import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Chip,
  LinearProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Fuse from "fuse.js";
import { RootState, AppDispatch } from "../../../redux/store";
import { fetchOrganizations } from "../../../redux/actions/organizationActions";
import {
  bulkUpdateDeployments,
  BulkDeploymentOrganizationResult,
} from "../../../redux/actions/deploymentActions";
import { toast } from "react-toastify";

interface BulkDeploymentPanelProps {
  buildId: string;
  buildName: string;
}

interface OrganizationRow {
  id: string;
  name: string;
  currentBuildId: string;
  currentBuildName: string;
}

const BulkDeploymentPanel: React.FC<BulkDeploymentPanelProps> = ({
  buildId,
  buildName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { organizations, loading: orgsLoading } = useSelector(
    (state: RootState) => state.organizations
  );

  const [selectedOrgs, setSelectedOrgs] = useState<GridRowSelectionModel>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentResults, setDeploymentResults] = useState<
    BulkDeploymentOrganizationResult[]
  >([]);
  const [organizationRows, setOrganizationRows] = useState<OrganizationRow[]>(
    []
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch organizations (which includes active_deployment with build info)
  useEffect(() => {
    dispatch(fetchOrganizations());
  }, [dispatch]);

  // Build organization rows with current build info from active_deployment
  useEffect(() => {
    if (organizations.length > 0) {
      const rows: OrganizationRow[] = organizations.map((org) => {
        const currentBuildId = org.active_deployment?.build?.id || "N/A";
        const currentBuildName = org.active_deployment?.build?.build_name || "No Deployment";

        return {
          id: org.id,
          name: org.name,
          currentBuildId,
          currentBuildName,
        };
      });
      setOrganizationRows(rows);
    }
  }, [organizations]);

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(organizationRows, {
      keys: ["name", "currentBuildId", "currentBuildName"],
      threshold: 0.5,
      includeScore: true,
    });
  }, [organizationRows]);

  // Filter organizations based on search query
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) {
      return organizationRows;
    }

    const results = fuse.search(searchQuery);
    return results.map((result) => result.item);
  }, [organizationRows, searchQuery, fuse]);

  const handleSelectAll = () => {
    // Only select organizations that are not already on this build
    const selectableOrgs = organizationRows
      .filter((row) => row.currentBuildId !== buildId)
      .map((row) => row.id);
    setSelectedOrgs(selectableOrgs);
  };

  const handleDeselectAll = () => {
    setSelectedOrgs([]);
  };

  const handleDeployClick = () => {
    if (selectedOrgs.length === 0) {
      toast.error("Please select at least one organization");
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmDeploy = async () => {
    setConfirmDialogOpen(false);
    setProgressDialogOpen(true);
    setIsDeploying(true);
    setDeploymentResults([]);

    try {
      const result = await dispatch(
        bulkUpdateDeployments({
          build_id: buildId,
          organization_ids: selectedOrgs as string[],
        })
      ).unwrap();

      setDeploymentResults(result.results);
      setIsDeploying(false);

      // Refetch organizations to update the UI with new build info
      dispatch(fetchOrganizations());

      // Show summary toast
      if (result.successful > 0 && result.failed === 0) {
        toast.success(
          `Successfully deployed to ${result.successful} organization(s)`
        );
      } else if (result.successful > 0 && result.failed > 0) {
        toast.warning(
          `Deployed to ${result.successful} organization(s), ${result.failed} failed`
        );
      } else {
        toast.error(`All ${result.failed} deployment(s) failed`);
      }
    } catch (error: any) {
      setIsDeploying(false);
      toast.error(`Bulk deployment failed: ${error.message || error}`);
      setProgressDialogOpen(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseProgress = () => {
    setProgressDialogOpen(false);
    setSelectedOrgs([]);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Organization Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "currentBuildName",
      headerName: "Current Build Name",
      flex: 2,
      minWidth: 300,
    },
    {
      field: "currentBuildId",
      headerName: "Current Build ID",
      flex: 1,
      minWidth: 250,
    },
    {
      field: "id",
      headerName: "Organization ID",
      flex: 0.8,
      minWidth: 200,
    },
  ];

  if (orgsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Deploy to Organizations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select organizations to deploy build <strong>{buildName}</strong> (
        {buildId})
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by Organization Name, Current Build ID, or Current Build Name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button variant="outlined" size="small" onClick={handleSelectAll}>
          Select All
        </Button>
        <Button variant="outlined" size="small" onClick={handleDeselectAll}>
          Deselect All
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleDeployClick}
          disabled={selectedOrgs.length === 0}
        >
          Deploy to {selectedOrgs.length} Selected Organization
          {selectedOrgs.length !== 1 ? "s" : ""}
        </Button>
      </Box>

      <Box sx={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedOrgs}
          onRowSelectionModelChange={(newSelection) => {
            setSelectedOrgs(newSelection);
          }}
          isRowSelectable={(params) => params.row.currentBuildId !== buildId}
          getRowClassName={(params) =>
            params.row.currentBuildId === buildId ? "already-deployed" : ""
          }
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          sx={{
            "& .already-deployed": {
              backgroundColor: "#f5f5f5",
              opacity: 0.6,
              fontStyle: "italic",
            },
            "& .already-deployed:hover": {
              backgroundColor: "#eeeeee",
            },
          }}
        />
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Bulk Deployment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to deploy build <strong>{buildName}</strong> (
            {buildId}) to <strong>{selectedOrgs.length}</strong> organization
            {selectedOrgs.length !== 1 ? "s" : ""}.
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            This will create new deployments for each selected organization,
            preserving their existing configuration (min/max hosts, behavior
            config, etc.) while updating only the build.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm}>Cancel</Button>
          <Button
            onClick={handleConfirmDeploy}
            variant="contained"
            color="primary"
            autoFocus
          >
            Confirm Deployment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Dialog */}
      <Dialog
        open={progressDialogOpen}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={isDeploying}
      >
        <DialogTitle>
          {isDeploying ? "Deploying..." : "Deployment Complete"}
        </DialogTitle>
        <DialogContent>
          {isDeploying && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Updating deployments for {selectedOrgs.length} organization
                {selectedOrgs.length !== 1 ? "s" : ""}...
              </Typography>
            </Box>
          )}

          {deploymentResults.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Results:
              </Typography>
              {deploymentResults.map((result) => (
                <Box
                  key={result.organization_id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: result.success
                      ? "#f0f7ff"
                      : "#fff5f5",
                  }}
                >
                  {result.success ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">
                      <strong>{result.organization_name}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {result.organization_id}
                    </Typography>
                  </Box>
                  {result.success ? (
                    <Chip
                      label="Success"
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      label={result.error_message || "Failed"}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              ))}

              {!isDeploying && (
                <Alert
                  severity={
                    deploymentResults.every((r) => r.success)
                      ? "success"
                      : deploymentResults.some((r) => r.success)
                        ? "warning"
                        : "error"
                  }
                  sx={{ mt: 2 }}
                >
                  {deploymentResults.filter((r) => r.success).length} of{" "}
                  {deploymentResults.length} deployment(s) successful
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseProgress}
            disabled={isDeploying}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkDeploymentPanel;
